// Hotel Guest Assistant — guest shell (Phase B).
// Flow: card tap → NEO routing → /assistant/:cardId → language → room number
// → 12h session → guest menu (enabled capabilities only) → module screens.
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Shield, Info, UtensilsCrossed, ConciergeBell, Tv, Loader2, Sparkles, ChevronRight, Mail, BellRing, Car, History, Gift, Menu as MenuIcon,
    Megaphone, MessageSquare, DoorOpen, Users, Building2, Recycle, LayoutGrid,
} from 'lucide-react';

// Condo resident-tile visual identity (neon accent). The ICON comes from the
// shared SERVICE_ICONS map so a tile and its request's progress bar can never
// drift apart.
const CONDO_TILE_VIS = {
    weather_event: { accent: 'blue' },
    waste_recycling: { accent: 'green' },
    parking: { accent: 'blue' },
    building: { accent: 'purple' },
    maintenance: { accent: 'orange' },
    security: { accent: 'rose' },
    amenities: { accent: 'yellow' },
    building_information: { accent: 'cyan' },
    concierge_front_desk: { accent: 'pink' },
};
const CONDO_ICON_CLS = 'w-5 h-5 sm:w-6 sm:h-6';
// Stock property tiles that are primary unless a property admin says otherwise.
// Chosen from real request volume (security and maintenance are the two that
// actually generate resident requests), not from taste.
const CONDO_DEFAULT_PRIORITY = ['maintenance', 'security'];
import { LanguageGate, ChangeLanguageLink } from '../../components/shared/LanguageGate';
import { cardSurfaceClass } from '../../components/shared/cardSurface';
import BrandingShell from '../../components/shared/BrandingShell';
import ExternalDestinationViewer from '../../components/shared/ExternalDestinationViewer';
import EntertainmentScreen from './EntertainmentScreen';
import ValetScreen from './ValetScreen';
import UnitMessageScreen from './UnitMessageScreen';
import AnnouncementsScreen from './AnnouncementsScreen';
import { PropertyPeopleScreen } from './PropertyPeopleScreen';
import { VisitorsScreen } from './VisitorsScreen';
import { TEXT, tl } from './assistantText';
import { captureDemoToken, getDemoToken } from '../../lib/demoMode';
import {
    SecurityScreen, HotelInfoScreen, FoodScreen, HousekeepingScreen,
    ConciergeScreen, MessagesScreen, ServiceTile, BroomIcon, ActivityScreen, OffersScreen, PoolTerraceScreen, PropertyTilesScreen,
    computeServiceStatus, readCleared, SERVICE_ICONS, HomeRequestsSummary,
} from './AssistantScreens';
import { PropertyGroupScreen } from './PropertyGroupScreen';
import { residentVisitorsAPI } from '../../lib/api';

const API = typeof window !== 'undefined' ? window.location.origin : '';
const LANGS = ['en', 'fr', 'es'];
const RESUMABLE = ['menu', 'security', 'info', 'food', 'housekeeping', 'concierge', 'messages', 'entertainment', 'valet', 'activity', 'offers', 'unit_messages', 'visitors'];
let ACTIVE_STORE_KEY = null; // hotel-wide once org is known (all cards share the stay)
const storeKey = (cardId) => ACTIVE_STORE_KEY || `yulontap_assistant:${cardId}`;

// Full-screen gate for active unread PRIORITY hotel messages.
// The guest must acknowledge each one before entering the experience.
const PriorityGate = ({ msgs, tx, busy, onAck }) => {
    const m = msgs[0];
    return (
        <div className="bg-rose-500/10 border border-rose-400/40 rounded-2xl p-5" data-testid="assistant-priority-gate">
            <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-xl bg-rose-500/25 text-rose-300 flex items-center justify-center shrink-0">
                    <BellRing className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-rose-300 text-[11px] font-bold uppercase tracking-widest">{tx.priorityBadge}</p>
                    <p className="text-white font-semibold text-sm">{tx.priorityHeading}</p>
                </div>
                {msgs.length > 1 && (
                    <span className="text-slate-300 text-xs shrink-0" data-testid="assistant-priority-count">1 / {msgs.length}</span>
                )}
            </div>
            {m.title && <p className="text-white font-bold text-base mt-4" data-testid="assistant-priority-title">{m.title}</p>}
            {m.message && (
                <p className="text-slate-200 text-sm mt-2 whitespace-pre-wrap" data-testid="assistant-priority-message">{m.message}</p>
            )}
            <button type="button" onClick={() => onAck(m.id)} disabled={busy}
                className="mt-5 w-full py-3.5 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold transition-colors"
                data-testid="assistant-priority-ack-btn">
                {busy ? '…' : tx.priorityAck}
            </button>
            {msgs.length > 1 && (
                <p className="text-slate-400 text-xs text-center mt-3" data-testid="assistant-priority-more">{tx.priorityMore}</p>
            )}
        </div>
    );
};

const readStore = (cardId) => {
    try { return JSON.parse(localStorage.getItem(storeKey(cardId))) || {}; } catch { return {}; }
};
const writeStore = (cardId, patch) => {
    try {
        const cur = readStore(cardId);
        localStorage.setItem(storeKey(cardId), JSON.stringify({ ...cur, ...patch }));
    } catch { /* private mode */ }
};

export default function HotelAssistant() {
    const { cardId } = useParams();
    const [phase, setPhase] = useState('loading');
    // "Full Menu" — a VIEW toggle only. Same tiles, same config, same sizing:
    // it just ungroups the primary/secondary tiers into one flat grid at the
    // full primary touch-target size. Tiered stays the default.
    const [fullMenu, setFullMenu] = useState(false);
    const [activeTile, setActiveTile] = useState(null);
    const [activeGroup, setActiveGroup] = useState(null);
    const [poolFullMenu, setPoolFullMenu] = useState(false);
    const [info, setInfo] = useState(null);
    const [lang, setLang] = useState('en');
    const [session, setSession] = useState(null);
    const [roomInput, setRoomInput] = useState('');
    const [roomError, setRoomError] = useState('');
    const [nameInput, setNameInput] = useState('');
    const [daysSel, setDaysSel] = useState(1);
    const [daysOther, setDaysOther] = useState('');
    const [pinInput, setPinInput] = useState('');
    // Phase 2 — condo resident two-step access ('identify' | 'create' | 'enter').
    // Hotel path never uses this (it keeps the single room-session submit).
    const [residentStep, setResidentStep] = useState('identify');
    const [busy, setBusy] = useState(false);
    // DEMO ONLY — set when the hotel demo launcher found no guest yet, so the
    // registration screen can offer a one-tap check-in. Null on every real card.
    const [demoGuestOffer, setDemoGuestOffer] = useState(null);
    // Same idea for the property demo's resident-access screen.
    const [demoTenantOffer, setDemoTenantOffer] = useState(null);
    const [dest, setDest] = useState(null);
    const [comm, setComm] = useState({ priority_pending: [], unread_count: 0 });
    const [activity, setActivity] = useState([]);
    const [hasOffers, setHasOffers] = useState(false);
    // Unread visitor conversations at this unit (the home-screen badge).
    const [visitorUnread, setVisitorUnread] = useState(0);

    const cfg = session?.config || info?.config || {};
    const _txBase = TEXT[lang] || TEXT.en;
    // Property-aware terminology: for non-hotel property types, override the
    // hotel-specific guest wording (Room→Unit, platform title, entry prompts)
    // using the existing terminology pack. Hotel keeps _txBase exactly as-is.
    const isCondo = !!cfg.property_type && cfg.property_type !== 'hotel';
    const tx = isCondo ? {
        ..._txBase,
        title: _txBase.residentAssistTitle || _txBase.title,
        room: (cfg.terminology?.unit && cfg.terminology.unit[lang]) || _txBase.room,
        roomTitle: _txBase.roomTitleUnit || _txBase.roomTitle,
        roomHint: _txBase.roomHintUnit || _txBase.roomHint,
        roomPlaceholder: _txBase.roomPlaceholderUnit || _txBase.roomPlaceholder,
        changeRoom: _txBase.changeUnit || _txBase.changeRoom,
        activityTag: _txBase.activityTagUnit || _txBase.activityTag,
        activity: _txBase.activityUnit || _txBase.activity,
        residentNotVerified: _txBase.residentNotVerified,
        residentIdentifyHint: _txBase.residentIdentifyHint,
        residentPinCreateHint: _txBase.residentPinCreateHint,
        residentPinEnterHint: _txBase.residentPinEnterHint,
        pinEnterTitle: _txBase.pinEnterTitle,
    } : _txBase;
    // Card Settings language config (existing YULONTAP resolver) is the source
    // of truth for which languages the guest may pick.
    const langsEnabled = (info?.enabled_languages && info.enabled_languages.length)
        ? info.enabled_languages : LANGS;
    // Card surface treatment (Inherit/Frosted/Clear/Solid) over the wallpaper.
    const surface = cardSurfaceClass(info?.card_style, 'dark');
    const openDest = useCallback((url, title) => { if (url) setDest({ url, title }); }, []);

    // Navigation-state preservation: guests returning from an external site
    // (or an accidental reload) land back in the same module, never at zero.
    useEffect(() => {
        if (RESUMABLE.includes(phase)) writeStore(cardId, { phase });
    }, [phase, cardId]);

    useEffect(() => {
        let cancelled = false;
        captureDemoToken(cardId);
        (async () => {
            try {
                const r = await fetch(`${API}/api/assistant/${cardId}/info`);
                if (!r.ok) throw new Error('info failed');
                const data = await r.json();
                if (cancelled) return;
                if (data.organization_id) { ACTIVE_STORE_KEY = `yulontap_stay:${data.organization_id}`; }
                setInfo(data);
                if (!data.config?.enabled) { setPhase('unavailable'); return; }

                const stored = readStore(cardId);
                const langs = (data.enabled_languages && data.enabled_languages.length)
                    ? data.enabled_languages : LANGS;
                let langChosen = null;
                if (stored.lang && langs.includes(stored.lang)) { setLang(stored.lang); langChosen = stored.lang; }
                else if (langs.length === 1) {
                    // Single-language hotel: no picker, resolver decides.
                    setLang(langs[0]);
                    writeStore(cardId, { lang: langs[0] });
                    langChosen = langs[0];
                }

                let sess = null;
                if (stored.token) {
                    try {
                        const sr = await fetch(`${API}/api/assistant/${cardId}/session/${stored.token}`);
                        if (sr.ok) {
                            const sd = await sr.json();
                            sess = { ...sd, token: stored.token };
                        } else {
                            writeStore(cardId, { token: null });
                        }
                    } catch { /* treat as no session */ }
                }
                if (cancelled) return;
                if (sess) {
                    setSession(sess);
                    if (sess.comm) setComm(sess.comm);
                } else {
                    // No stay yet. If the hotel demo launcher sent us here,
                    // offer the one-tap demo guest next to the real form.
                    let wants = false;
                    try { wants = sessionStorage.getItem('yulontap_hotel_demo_offer_guest') === '1'; } catch (_e) { /* ignore */ }
                    if (wants) {
                        try {
                            const ir = await fetch(`${API}/api/demo-hotel/identity`);
                            const id = ir.ok ? await ir.json() : null;
                            if (!cancelled && id && (id.card_ids || []).includes(cardId)) {
                                setDemoGuestOffer({ room: id.demo_guest_room || '105' });
                            }
                        } catch (_e) { /* the real form still works */ }
                    }
                    let wantsTenant = false;
                    try { wantsTenant = sessionStorage.getItem('yulontap_property_demo_offer_tenant') === '1'; } catch (_e) { /* ignore */ }
                    if (wantsTenant && cardId === 'CONDOCARD-a-0001' && !cancelled) {
                        setDemoTenantOffer({ unit: '101' });
                    }
                }
                if (!langChosen) setPhase('language');
                else if (sess) {
                    // Coming back from another demo role: straight to the guest's
                    // HOME, where their requests and statuses are listed. The
                    // "Welcome back" interstitial is for a guest returning later,
                    // not for a presenter switching views.
                    if (stored.switchResume) {
                        writeStore(cardId, { switchResume: false });
                        setPhase('menu');
                    } else if (RESUMABLE.includes(stored.phase) && stored.phase !== 'menu') setPhase(stored.phase);
                    else setPhase(sess.guest_name ? 'welcomeback' : 'menu');
                } else setPhase('room');
            } catch {
                if (!cancelled) setPhase('error');
            }
        })();
        return () => { cancelled = true; };
    }, [cardId]);

    const handleLanguage = (code) => {
        setLang(code);
        writeStore(cardId, { lang: code });
        setPhase(session ? 'menu' : 'room');
    };

    const handleExpired = useCallback(() => {
        writeStore(cardId, { token: null });
        setSession(null);
        setRoomError(TEXT[lang]?.sessionExpired || TEXT.en.sessionExpired);
        setPhase('room');
    }, [cardId, lang]);

    const post = useCallback(async (path, body) => {
        const r = await fetch(`${API}/api/assistant/${cardId}/${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, token: session?.token }),
        });
        if (r.status === 404 || r.status === 410) { handleExpired(); throw new Error('expired'); }
        if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            throw new Error(d.detail || 'request failed');
        }
        return r.json();
    }, [cardId, session, handleExpired]);

    // One-tap demo check-in. The stay is written HERE, through the same
    // writeStore the Assistant reads from, so the launcher can never write to a
    // key this screen doesn't read (that mismatch is what sent the guest back
    // to registration mid-demo).
    const continueAsDemoGuest = async () => {
        setBusy(true);
        setRoomError('');
        try {
            const r = await fetch(`${API}/api/demo-hotel/guest/session`);
            const d = await r.json().catch(() => ({}));
            if (!r.ok || !d.token) throw new Error('demo guest');
            writeStore(cardId, { token: d.token, lang });
            const sr = await fetch(`${API}/api/assistant/${cardId}/session/${d.token}`);
            if (!sr.ok) throw new Error('session');
            const sd = await sr.json();
            setSession({ ...sd, token: d.token });
            if (sd.comm) setComm(sd.comm);
            try { sessionStorage.removeItem('yulontap_hotel_demo_offer_guest'); } catch (_e) { /* ignore */ }
            setDemoGuestOffer(null);
            setPhase('menu');
        } catch (_e) {
            setRoomError(tx.roomError || 'Could not open the demo guest');
        } finally { setBusy(false); }
    };

    // One-tap demo tenant, mirroring the hotel guest: the stay is written HERE,
    // through the same writeStore the portal reads from.
    const continueAsDemoTenant = async () => {
        setBusy(true);
        setRoomError('');
        try {
            const r = await fetch(`${API}/api/demo-tenant/session`);
            const d = await r.json().catch(() => ({}));
            if (!r.ok || !d.token) throw new Error('demo tenant');
            writeStore(cardId, { token: d.token, lang });
            const sr = await fetch(`${API}/api/assistant/${cardId}/session/${d.token}`);
            if (!sr.ok) throw new Error('session');
            const sd = await sr.json();
            setSession({ ...sd, token: d.token });
            if (sd.comm) setComm(sd.comm);
            try { sessionStorage.removeItem('yulontap_property_demo_offer_tenant'); } catch (_e) { /* ignore */ }
            setDemoTenantOffer(null);
            setPhase('menu');
        } catch (_e) {
            setRoomError(tx.roomError || 'Could not open the demo tenant');
        } finally { setBusy(false); }
    };

    const handleRoomSubmit = async (e) => {
        e.preventDefault();
        const room = ((info?.room_number || '').trim()) || roomInput.trim();
        const name = nameInput.trim();
        const days = daysSel === 'other' ? parseInt(daysOther, 10) : daysSel;
        if (!room) { setRoomError(tx.roomInvalid); return; }
        // Demo orgs accept ANY 3-digit room — say so instead of a generic error.
        if (info?.demo_entry && !/^\d{3}$/.test(room)) { setRoomError(tx.demoRoomFormat); return; }
        if (!name) { setRoomError(tx.nameRequired); return; }
        if (!days || days < 1 || days > 60) { setRoomError(tx.daysRequired); return; }
        if (!/^\d{4}$/.test(pinInput)) { setRoomError(info?.demo_entry ? tx.demoPinFormat : tx.pinRequired); return; }
        setBusy(true);
        setRoomError('');
        try {
            const r = await fetch(`${API}/api/assistant/${cardId}/room-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ room_number: room, first_name: name, stay_days: days, pin: pinInput, language: lang, demo_token: getDemoToken(cardId) }),
            });
            if (r.status === 404) { setRoomError(tx.roomNotFound); return; }
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                if (d.detail === 'demo_room_format') { setRoomError(tx.demoRoomFormat); return; }
                if (d.detail === 'demo_pin_format') { setRoomError(tx.demoPinFormat); return; }
                setRoomError(tx.roomInvalid); return;
            }
            const data = await r.json();
            setSession({ ...data, room_number: data.room_number });
            if (data.comm) setComm(data.comm);
            writeStore(cardId, { token: data.token });
            setPhase('menu');
        } catch {
            setRoomError(tx.tryAgain);
        } finally {
            setBusy(false);
        }
    };

    // Phase 2 — condo resident access (persistent PIN). Property-type gated;
    // hotel keeps handleRoomSubmit above. Step 1 identifies unit+name → backend
    // says create_pin (first access) or enter_pin (returning). Step 2 sends PIN.
    const handleResidentSubmit = async (e) => {
        e.preventDefault();
        const unit = ((info?.room_number || '').trim()) || roomInput.trim();
        const name = nameInput.trim();
        // Demo orgs accept ANY 3-digit unit (or a seeded unit label).
        if (info?.demo_entry && residentStep === 'identify' && unit && !/^\d{3}$/.test(unit)
            && !/^[A-Za-z]-?\d{3}$/.test(unit)) { setRoomError(tx.demoRoomFormat); return; }
        if (!unit) { setRoomError(tx.roomInvalid); return; }
        if (!name) { setRoomError(tx.nameRequired); return; }
        const sendingPin = residentStep !== 'identify';
        if (sendingPin && !/^\d{4}$/.test(pinInput)) { setRoomError(info?.demo_entry ? tx.demoPinFormat : tx.pinRequired); return; }
        setBusy(true);
        setRoomError('');
        try {
            const body = { unit, name, language: lang, demo_token: getDemoToken(cardId) };
            if (sendingPin) body.pin = pinInput;
            const r = await fetch(`${API}/api/assistant/${cardId}/resident-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (r.status === 429) { setRoomError(data.detail || tx.tryAgain); return; }
            if (data.detail === 'demo_room_format') { setRoomError(tx.demoRoomFormat); return; }
            if (data.detail === 'demo_pin_format') { setRoomError(tx.demoPinFormat); return; }
            if (!r.ok) { setRoomError(tx.residentNotVerified || data.detail || tx.roomInvalid); return; }
            if (data.status === 'create_pin') { setResidentStep('create'); setPinInput(''); return; }
            if (data.status === 'enter_pin') { setResidentStep('enter'); setPinInput(''); return; }
            if (data.status === 'authenticated') {
                setSession({ ...data, room_number: data.room_number });
                if (data.comm) setComm(data.comm);
                writeStore(cardId, { token: data.token });
                setResidentStep('identify');
                setPinInput('');
                setPhase('menu');
            }
        } catch {
            setRoomError(tx.tryAgain);
        } finally {
            setBusy(false);
        }
    };

    const changeRoom = () => {
        writeStore(cardId, { token: null });
        setSession(null);
        setComm({ priority_pending: [], unread_count: 0 });
        setRoomInput('');
        setNameInput('');
        setDaysSel(1);
        setDaysOther('');
        setPinInput('');
        setResidentStep('identify');
        setRoomError('');
        setPhase('room');
    };

    // Priority gate — server is the source of truth (session start/resume +
    // comm-status refresh); acknowledgement recorded on the existing message doc.
    const refreshComm = useCallback(() => {
        if (!session?.token) return;
        fetch(`${API}/api/assistant/${cardId}/comm-status?token=${encodeURIComponent(session.token)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (d && d.priority_pending) setComm(d); })
            .catch(() => {});
    }, [cardId, session]);

    // Chunk 2 — decide whether to show the conditional "Offers & Gift Cards"
    // tile (existing Voucher Engine). Hidden entirely when there are none.
    useEffect(() => {
        if (!session?.token) { setHasOffers(false); return; }
        fetch(`${API}/api/assistant/${cardId}/offers?token=${encodeURIComponent(session.token)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => setHasOffers(!!(d && (d.offers || []).length > 0)))
            .catch(() => {});
    }, [cardId, session]);

    // Per-service request status for the menu tile dots (reuses /activity; RED
    // open / AMBER acknowledged / GREEN resolved). Excludes user-cleared items.
    const svcStatus = computeServiceStatus(activity, readCleared(cardId), isCondo);
    useEffect(() => {
        if (!session?.token) { setActivity([]); return; }
        let alive = true;
        const load = () => fetch(`${API}/api/assistant/${cardId}/activity?token=${encodeURIComponent(session.token)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d) setActivity(d.activity || []); })
            .catch(() => {});
        load();
        const iv = setInterval(load, 5000);
        // Hidden tabs get their timers throttled, so refresh the guest's request
        // progress the moment this view is looked at again (no manual reload).
        const wake = () => { if (document.visibilityState === 'visible') load(); };
        document.addEventListener('visibilitychange', wake);
        window.addEventListener('focus', wake);
        return () => {
            alive = false;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', wake);
            window.removeEventListener('focus', wake);
        };
    }, [cardId, session]);

    // Unread visitor conversations for THIS unit — the home badge. Resident
    // credential only (property portal), polled like the request activity.
    useEffect(() => {
        if (!isCondo || !session?.token) { setVisitorUnread(0); return; }
        let alive = true;
        const load = () => residentVisitorsAPI.list(session.token)
            .then((r) => { if (alive) setVisitorUnread(r.data?.unread_total || 0); })
            .catch(() => {});
        load();
        const iv = setInterval(load, 15000);
        return () => { alive = false; clearInterval(iv); };
    }, [isCondo, session]);

    const ackPriority = async (messageId) => {        setBusy(true);
        try {
            const r = await fetch(`${API}/api/assistant/${cardId}/messages/${messageId}/acknowledge`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: session?.token }),
            });
            if (r.status === 404 || r.status === 410) { handleExpired(); return; }
            const d = await r.json();
            if (d.comm) setComm(d.comm);
        } catch { /* gate stays until server confirms */ }
        finally { setBusy(false); }
    };

    const wrap = (children, width = 'max-w-md') => (
        <BrandingShell wallpaperUrl={info?.background_image_url || null}
            wallpaperZoom={info?.background_image_scale || null} variant="dark" centered={false}
            data-testid="hotel-assistant-page">
            <div className="min-h-screen flex flex-col items-center p-4 sm:p-6 relative">
                {langsEnabled.length > 1 && (
                    <ChangeLanguageLink enabled={langsEnabled} variant="dark"
                        onOpen={() => { writeStore(cardId, { lang: null }); setPhase('language'); }} />
                )}
                <div className={`w-full ${width} mx-auto flex-1 flex flex-col justify-center py-8`}>
                    {cfg.sponsor?.enabled && cfg.sponsor?.name && (cfg.sponsor?.position || 'bottom') === 'top' && (
                        <div className="w-full flex flex-col items-center gap-1 pb-4" data-testid="assistant-sponsor-bar-top">
                            <span className="text-slate-400 text-xs">{tl(cfg.sponsor, 'prefix', lang) || 'Presented by'}</span>
                            {cfg.sponsor.logo_url && (
                                <img src={cfg.sponsor.logo_url} alt=""
                                    style={cfg.sponsor.logo_size ? { height: `${Math.min(200, cfg.sponsor.logo_size)}px` } : undefined}
                                    className={`${cfg.sponsor.logo_size ? '' : 'max-h-6'} object-contain max-w-[85%]`}
                                    data-testid="assistant-sponsor-logo" />
                            )}
                            <span className="text-slate-200 text-xs font-medium" data-testid="assistant-sponsor-name">{cfg.sponsor.name}</span>
                        </div>
                    )}
                    {children}
                </div>
                {cfg.sponsor?.enabled && cfg.sponsor?.name && (cfg.sponsor?.position || 'bottom') === 'bottom' && (
                    <div className="w-full max-w-md mx-auto flex items-center justify-center gap-2 py-3 border-t border-white/10"
                        data-testid="assistant-sponsor-bar">
                        <span className="text-slate-400 text-xs">{tl(cfg.sponsor, 'prefix', lang) || 'Presented by'}</span>
                        {cfg.sponsor.logo_url && (
                            <img src={cfg.sponsor.logo_url} alt=""
                                style={cfg.sponsor.logo_size ? { height: `${Math.min(200, cfg.sponsor.logo_size)}px` } : undefined}
                                className={`${cfg.sponsor.logo_size ? '' : 'max-h-6'} object-contain max-w-[200px]`}
                                data-testid="assistant-sponsor-logo" />
                        )}
                        <span className="text-slate-200 text-xs font-medium" data-testid="assistant-sponsor-name">{cfg.sponsor.name}</span>
                    </div>
                )}
            </div>
            {dest && (
                <ExternalDestinationViewer url={dest.url} title={dest.title} tx={tx}
                    onClose={() => setDest(null)} />
            )}
        </BrandingShell>
    );

    // Visual hierarchy: wallpaper → hotel logo (up to 240px, admin-sized) →
    // discreet GAP title. The hotel brand dominates, never the platform name.
    const header = (
        <div className="text-center mb-6">
            {info?.logo_url && (
                <img src={info.logo_url} alt=""
                    style={info?.logo_size ? { height: `${info.logo_size}px`, maxHeight: '240px' } : undefined}
                    className={`${info?.logo_size ? '' : 'max-h-16'} max-w-[300px] object-contain mx-auto mb-3`}
                    data-testid="assistant-hotel-logo" />
            )}
            {cardId === 'CONDOCARD-a-0001' && (
                <img src="/maple-properties-logo.png" alt="Maple Court Properties"
                    data-testid="maple-logo-tenant"
                    className="mx-auto mb-3 h-16 w-auto object-contain" />
            )}
            <p className="text-emerald-400/70 text-xs uppercase tracking-widest" data-testid="assistant-hotel-name">{info?.hotel_name}</p>
            <h1 className="text-base sm:text-lg font-semibold text-slate-200 mt-1 tracking-wide"
                style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="assistant-gap-title">
                {tx.title}
            </h1>
        </div>
    );

    if (phase === 'loading') return wrap(
        <div className="flex justify-center" data-testid="assistant-loading">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
    );

    if (phase === 'error') return wrap(
        <div className="text-center" data-testid="assistant-error">
            <p className="text-white font-semibold">{tx.errorTitle}</p>
            <button type="button" onClick={() => window.location.reload()}
                className="mt-4 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                data-testid="assistant-retry-btn">{tx.tryAgain}</button>
        </div>
    );

    if (phase === 'unavailable') return wrap(
        <p className="text-slate-300 text-center" data-testid="assistant-unavailable">{tx.notAvailable}</p>
    );

    if (phase === 'language') return wrap(
        <>
            {header}
            <LanguageGate enabled={langsEnabled} onSelect={handleLanguage} variant="dark" embedded
                cardStyle={info?.card_style || 'frosted'} />
        </>
    );

    if (phase === 'room' && isCondo) return wrap(
        <>
            {header}
            <form onSubmit={handleResidentSubmit} className={`${surface} rounded-2xl p-6`} data-testid="resident-access-form">
                <p className="text-white font-semibold">{tx.roomTitle}</p>
                <p className="text-slate-400 text-sm mt-1 mb-4">{tx.residentIdentifyHint || tx.roomHint}</p>
                {(info?.room_number || '').trim() ? (
                    <div className="w-full rounded-xl bg-emerald-900/30 border border-emerald-500/30 text-white text-lg text-center py-3 px-4 font-semibold"
                        data-testid="resident-unit-fixed">
                        {tx.room} {info.room_number}
                    </div>
                ) : (
                    <input type="text" inputMode="text" autoComplete="off" maxLength={10}
                        value={roomInput} onChange={(e) => setRoomInput(e.target.value)}
                        placeholder={tx.roomPlaceholder}
                        disabled={residentStep !== 'identify'}
                        className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-lg text-center py-3 px-4 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 disabled:opacity-60"
                        data-testid="resident-unit-input" />
                )}
                <input type="text" autoComplete="off" maxLength={40}
                    value={nameInput} onChange={(e) => setNameInput(e.target.value)}
                    placeholder={tx.firstName}
                    disabled={residentStep !== 'identify'}
                    className="mt-3 w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-base text-center py-3 px-4 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 disabled:opacity-60"
                    data-testid="resident-name-input" />
                {residentStep !== 'identify' && (
                    <>
                        <p className="text-white text-sm font-bold mt-4 mb-1.5 text-center tracking-wide"
                            data-testid="resident-pin-title">
                            {residentStep === 'create' ? tx.pinCreateTitle : tx.pinEnterTitle}
                        </p>
                        <input type="tel" inputMode="numeric" autoComplete="off" maxLength={4}
                            value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                            placeholder={tx.pinLabel} autoFocus
                            className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-lg text-center tracking-[0.5em] py-3 px-4 placeholder:text-slate-500 placeholder:tracking-normal focus:outline-none focus:border-emerald-500"
                            data-testid="resident-pin-input" />
                        <p className="text-slate-500 text-[11px] mt-1.5 text-center">
                            {residentStep === 'create' ? tx.residentPinCreateHint : tx.residentPinEnterHint}
                        </p>
                    </>
                )}
                {roomError && <p className="text-rose-400 text-sm mt-2 text-center" data-testid="resident-access-error">{roomError}</p>}
                <button type="submit" disabled={busy}
                    className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-60"
                    data-testid="resident-access-submit-btn">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.continue}
                </button>
                {/* DEMO ONLY (property demo card): one-tap so a presenter does
                    not type a unit, a name and a PIN in front of an audience.
                    The real form above is untouched. */}
                {demoTenantOffer && (
                    <>
                        <button type="button" onClick={continueAsDemoTenant} disabled={busy}
                            className="mt-3 w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold transition-colors disabled:opacity-60"
                            data-testid="assistant-demo-tenant-btn">
                            {(tx.demoTenantCta || 'Continue as demo tenant (Unit {unit})').replace('{unit}', demoTenantOffer.unit)}
                        </button>
                        <p className="text-slate-500 text-[11px] mt-1.5 text-center">{tx.demoTenantHint || tx.demoGuestHint}</p>
                    </>
                )}
            </form>
        </>
    );

    if (phase === 'room') return wrap(
        <>
            {header}
            <form onSubmit={handleRoomSubmit} className={`${surface} rounded-2xl p-6`} data-testid="assistant-room-form">
                <p className="text-white font-semibold">{tx.roomTitle}</p>
                <p className="text-slate-400 text-sm mt-1 mb-4">{tx.roomHint}</p>
                {(info?.room_number || '').trim() ? (
                    <div className="w-full rounded-xl bg-emerald-900/30 border border-emerald-500/30 text-white text-lg text-center py-3 px-4 font-semibold"
                        data-testid="assistant-room-fixed">
                        {tx.room} {info.room_number}
                    </div>
                ) : (
                    <input type="text" inputMode="text" autoComplete="off" maxLength={10}
                        value={roomInput} onChange={(e) => setRoomInput(e.target.value)}
                        placeholder={tx.roomPlaceholder}
                        className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-lg text-center py-3 px-4 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                        data-testid="assistant-room-input" />
                )}
                <input type="text" autoComplete="off" maxLength={40}
                    value={nameInput} onChange={(e) => setNameInput(e.target.value)}
                    placeholder={tx.firstName}
                    className="mt-3 w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-base text-center py-3 px-4 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="assistant-name-input" />
                <p className="text-slate-400 text-xs mt-4 mb-2">{tx.stayHowLong}</p>
                <div className="flex flex-wrap gap-2" data-testid="assistant-stay-options">
                    {[1, 2, 3, 4, 5, 6, 7, 14, 21, 30].map((n) => (
                        <button key={n} type="button" onClick={() => setDaysSel(n)}
                            className={`px-3 py-2 rounded-full text-xs font-medium transition-colors border ${daysSel === n ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-white/5 border-white/15 text-slate-300'}`}
                            data-testid={`assistant-stay-${n}`}>
                            {n} {n === 1 ? tx.day : tx.days}
                        </button>
                    ))}
                    <button type="button" onClick={() => setDaysSel('other')}
                        className={`px-3 py-2 rounded-full text-xs font-medium transition-colors border ${daysSel === 'other' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-white/5 border-white/15 text-slate-300'}`}
                        data-testid="assistant-stay-other">
                        {tx.otherStay}
                    </button>
                </div>
                {daysSel === 'other' && (
                    <input type="number" min={1} max={60} value={daysOther}
                        onChange={(e) => setDaysOther(e.target.value)} placeholder={tx.daysPlaceholder}
                        className="mt-2 w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-base text-center py-2.5 px-4 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                        data-testid="assistant-stay-other-input" />
                )}
                <p className="text-white text-sm font-bold mt-4 mb-1.5 text-center tracking-wide"
                    data-testid="assistant-pin-create-title">{tx.pinCreateTitle}</p>
                <input type="tel" inputMode="numeric" autoComplete="off" maxLength={4}
                    value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder={tx.pinLabel}
                    className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-lg text-center tracking-[0.5em] py-3 px-4 placeholder:text-slate-500 placeholder:tracking-normal focus:outline-none focus:border-emerald-500"
                    data-testid="assistant-pin-input" />
                <p className="text-slate-500 text-[11px] mt-1.5 text-center">{tx.pinHint}</p>
                {roomError && <p className="text-rose-400 text-sm mt-2 text-center" data-testid="assistant-room-error">{roomError}</p>}
                <button type="submit" disabled={busy}
                    className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-60"
                    data-testid="assistant-room-submit-btn">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.continue}
                </button>
                {/* DEMO ONLY: a presenter should not have to type a room, a name
                    and a PIN in front of an audience. The real form above stays
                    exactly as it is. Shown only on the hotel DEMO card. */}
                {demoGuestOffer && (
                    <>
                        <button type="button" onClick={continueAsDemoGuest} disabled={busy}
                            className="mt-3 w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold transition-colors disabled:opacity-60"
                            data-testid="assistant-demo-guest-btn">
                            {(tx.demoGuestCta || 'Continue as demo guest (Room {room})').replace('{room}', demoGuestOffer.room)}
                        </button>
                        <p className="text-slate-500 text-[11px] mt-1.5 text-center">{tx.demoGuestHint}</p>
                    </>
                )}
            </form>
        </>
    );

    // Active unread Priority messages block the entire guest experience
    // (menu + every module) until acknowledged. Most recent first.
    if (session && (comm.priority_pending || []).length > 0) {
        return wrap(
            <>
                {header}
                <PriorityGate msgs={comm.priority_pending} tx={tx} busy={busy} onAck={ackPriority} />
            </>
        );
    }

    if (phase === 'welcomeback') return wrap(
        <>
            {header}
            <div className={`${surface} rounded-2xl p-6 text-center`} data-testid="assistant-welcomeback">
                <Sparkles className="w-8 h-8 text-emerald-400 mx-auto" />
                <p className="text-white font-bold text-xl mt-3" data-testid="assistant-welcomeback-name">
                    {tx.welcomeBack}, {session?.guest_name}
                </p>
                <button type="button" onClick={() => setPhase('menu')}
                    className="mt-5 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                    data-testid="assistant-welcomeback-continue">
                    {tx.continue}
                </button>
                <button type="button" onClick={changeRoom}
                    className="mt-3 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-300 text-sm font-medium transition-colors"
                    data-testid="assistant-not-you-btn">
                    {(tx.notYou || 'Not {name}?').replace('{name}', session?.guest_name || '')}
                </button>
            </div>
        </>
    );

    if (phase === 'menu') {
        // Guest Services Entry Context — Pool & Terrace lands directly on its
        // contextual menu (reuses the guest session; only the post-login view
        // changes). Standard/lobby/fitness fall through to the normal menu.
        if (info?.guest_services_context === 'pool_terrace' && !poolFullMenu) {
            return wrap(
                <>
                    {header}
                    <PoolTerraceScreen pool={info.pool_terrace || {}} tx={tx} post={post} surface={surface}
                        cardId={cardId} token={session?.token} api={API}
                        roomNumber={session?.room_number} onChangeRoom={changeRoom}
                        onFullMenu={() => setPoolFullMenu(true)} />
                </>,
                'max-w-md md:max-w-3xl lg:max-w-4xl'
            );
        }
        // Phase 5 — Entertainment available to Condo too (config-gated). Hotel
        // condition unchanged (was !false && …). Restaurant Partners are surfaced
        // inside the Entertainment screen (reuses food.outside_restaurants.partners).
        const entOn = cfg.entertainment?.enabled && (cfg.modules?.entertainment !== false);
        // Property/condo home is TWO TIERS, not one flat grid of shrunken tiles.
        // A resident was getting a SMALLER tap target than a hotel guest from the
        // identical component, purely because property routinely has 10-14 tiles.
        // Primary = the few residents actually use, at hotel size; everything else
        // keeps the EXISTING 44px compact size under a section label. Nothing is
        // ever shrunk further than today — it scrolls instead. Hotel is untouched:
        // it renders the same single grid, in the same order, at the same sizes.
        const menu = [];
        const push = (tier, node) => menu.push({ tier, node });
        // Property/condo secondary tier is GROUPED (property only — hotel has no
        // Tap Entry / People / Waste / Parking / Amenities tiles at all, and its
        // own default tile catalogue is empty, so none of this applies there).
        // Each group is one entry tile leading to a stacked sub-page.
        const groupRows = { communication: [], building_access: [], services: [] };
        const addRow = (g, row) => { if (isCondo) groupRows[g].push(row); };
        const openTile = (t) => { setActiveTile(t); setPhase('property_tiles'); };
        const compactFor = (tier) => isCondo && tier === 'secondary' && !fullMenu;
        const icls = (tier) => (compactFor(tier) ? CONDO_ICON_CLS : 'w-6 h-6 sm:w-7 sm:h-7');

        if (!isCondo && cfg.security?.enabled && (cfg.modules?.security !== false)) {
            push('primary', <ServiceTile key="security" icon={<Shield className={icls('primary')} />} title={tx.security} tag={tx.securityTag}
                accent="rose" surface={surface} statusColor={svcStatus.security}
                onClick={() => setPhase('security')} testId="assistant-menu-security" />);
        }
        if (!isCondo && cfg.hotel_info?.enabled && (cfg.hotel_info?.sections || []).length > 0) {
            push('primary', <ServiceTile key="info" icon={<Info className={icls('primary')} />} title={tx.hotelInfo} tag={tx.hotelInfoTag}
                accent="green" surface={surface} onClick={() => setPhase('info')} testId="assistant-menu-info" />);
        }
        if (!isCondo && cfg.food?.enabled && (cfg.modules?.food !== false)) {
            push('primary', <ServiceTile key="food" icon={<UtensilsCrossed className={icls('primary')} />} title={tx.food} tag={tx.foodTag}
                accent="amber" statusColor={svcStatus.food}
                surface={surface} onClick={() => setPhase('food')} testId="assistant-menu-food" />);
        }
        if (entOn) {
            push('secondary', <ServiceTile key="entertainment" icon={<Tv className={icls('secondary')} />}
                title={tl(cfg.entertainment, 'label', lang) || tx.entertainment} tag={tx.entertainmentTag}
                accent="purple" surface={surface} compact={compactFor('secondary')}
                onClick={() => setPhase('entertainment')} testId="assistant-menu-entertainment" />);
        }
        if (!isCondo && cfg.housekeeping?.enabled && (cfg.modules?.housekeeping !== false)) {
            push('primary', <ServiceTile key="housekeeping" icon={<BroomIcon className={icls('primary')} data-testid="assistant-icon-housekeeping-broom" />}
                title={tx.housekeeping} tag={tx.housekeepingTag}
                accent="yellow" statusColor={svcStatus.housekeeping}
                surface={surface} onClick={() => setPhase('housekeeping')} testId="assistant-menu-housekeeping" />);
        }
        if (!isCondo && cfg.concierge?.enabled && (cfg.modules?.concierge !== false)) {
            push('primary', <ServiceTile key="concierge" icon={<ConciergeBell className={icls('primary')} />} title={tx.concierge} tag={tx.conciergeTag}
                accent="teal" statusColor={svcStatus.concierge}
                surface={surface} onClick={() => setPhase('concierge')} testId="assistant-menu-concierge" />);
        }
        if (!isCondo && cfg.valet?.enabled && (cfg.modules?.valet !== false)) {
            push('primary', <ServiceTile key="valet" icon={<Car className={icls('primary')} />} title={tx.valet} tag={tx.valetTag}
                accent="violet" surface={surface} statusColor={svcStatus.valet}
                onClick={() => setPhase('valet')} testId="assistant-menu-valet" />);
        }
        if (cfg.communication?.enabled && (cfg.modules?.communication !== false)) {
            const commTitle = cfg.property_type === 'condominium_association'
                ? (cfg.terminology?.notices?.[lang] || tx.communityBoard)
                : tx.messages;
            // NOT a duplicate of Announcements: this is /messages — management
            // writing to THIS unit. Announcements is /group-messages — a building
            // wide broadcast. Same sender, different audience, different data, so
            // both live on, with tags that finally say which is which.
            if (isCondo) {
                addRow('communication', {
                    key: 'messages', icon: Megaphone, accent: 'purple',
                    label: commTitle, tag: tx.commUnitTag,
                    onClick: () => setPhase('messages'),
                });
            } else {
                push('secondary', <ServiceTile key="messages"
                    icon={<Mail className={icls('secondary')} />}
                    title={commTitle} tag={tx.messagesTag}
                    accent="orange" badge={comm.unread_count} surface={surface} compact={compactFor('secondary')}
                    onClick={() => setPhase('messages')} testId="assistant-menu-messages" />);
            }
        }
        if (cfg.modules?.unit_messaging) {
            // Message a Neighbour — primary for a resident (highest-frequency).
            push('primary', <ServiceTile key="unit_messages" icon={<MessageSquare className={icls('primary')} />}
                title={isCondo ? tx.umTileUnit : tx.umTile}
                tag={isCondo ? tx.umTagUnit : tx.umTag}
                accent="teal" surface={surface} compact={compactFor('primary')}
                onClick={() => setPhase('unit_messages')} testId="assistant-menu-unit-messages" />);
        }
        if (isCondo) {
            addRow('communication', {
                key: 'announcements', icon: Megaphone, accent: 'purple',
                label: tx.annTile, tag: tx.commAllTag,
                onClick: () => setPhase('announcements'),
            });
        } else if (cfg.property_type === 'hotel') {
            push('secondary', <ServiceTile key="announcements" icon={<Megaphone className={icls('secondary')} />}
                title={tx.annTile} tag={tx.annTag}
                accent="purple" surface={surface} compact={compactFor('secondary')}
                onClick={() => setPhase('announcements')} testId="assistant-menu-announcements" />);
        }
        // My Requests — primary in both verticals. Uses History in BOTH now: at
        // full size next to Message a Neighbour, two identical MessageSquare
        // glyphs read as a rendering bug.
        push('primary', <ServiceTile key="activity" icon={<History className={icls('primary')} />} title={tx.activity} tag={tx.activityTag}
            accent="green" surface={surface} compact={compactFor('primary')}
            onClick={() => setPhase('activity')} testId="assistant-menu-activity" />);
        if (isCondo && session?.location_id) {
            addRow('building_access', {
                key: 'tap_entry', icon: DoorOpen, accent: 'cyan',
                label: tx.tapEntryTile, tag: tx.tapEntryTag,
                onClick: () => { window.location.href = `/tap-entry/${session.location_id}`; },
            });
        }
        if (isCondo && session?.token) {
            addRow('communication', {
                key: 'visitors', icon: DoorOpen, accent: 'cyan',
                label: tx.vstTile,
                tag: visitorUnread > 0 ? `${visitorUnread} ${tx.vstNew}` : tx.vstTag,
                onClick: () => setPhase('visitors'),
            });
            addRow('building_access', {
                key: 'people', icon: Users, accent: 'cyan',
                label: tx.ppTile, tag: tx.ppTag,
                onClick: () => setPhase('people_actions'),
            });
        }
        if (hasOffers) {
            push('secondary', <ServiceTile key="offers" icon={<Gift className={icls('secondary')} />} title={tx.offers} tag={tx.offersTag}
                accent="green" surface={surface} compact={compactFor('secondary')}
                onClick={() => setPhase('offers')} testId="assistant-menu-offers" />);
        }
        // Which stock property tiles live inside which group. `weather_event` is
        // deliberately absent: it is a live event/alert, not a static info menu,
        // so it stays a standalone tile.
        const TILE_GROUP = {
            waste_recycling: 'services', parking: 'services', amenities: 'services',
            building: 'building_access', building_information: 'building_access',
        };
        (cfg.tiles || []).filter((t) => t.enabled !== false).forEach((t) => {
            const vis = CONDO_TILE_VIS[t.id];
            const TileIcon = SERVICE_ICONS[t.id] || MenuIcon;
            // A custom tile is never promoted by guesswork: only an explicit
            // `priority` flag (or the two stock tiles residents actually use)
            // reaches the primary tier.
            const tier = (t.priority === true
                || (t.priority === undefined && CONDO_DEFAULT_PRIORITY.includes(t.id))) ? 'primary' : 'secondary';
            const grp = tier === 'secondary' ? TILE_GROUP[t.id] : null;
            if (isCondo && grp) {
                addRow(grp, {
                    key: t.id, icon: TileIcon, accent: vis?.accent || 'teal',
                    label: tl(t, 'label', lang) || t.label_en,
                    tag: `${(t.subtiles || []).filter((s) => s.enabled !== false).length} ${tx.groupOptions}`,
                    onClick: () => openTile(t),
                });
                return;
            }
            push(tier, <ServiceTile key={t.id}
                icon={<TileIcon className={icls(tier)} />}
                title={tl(t, 'label', lang) || t.label_en}
                tag={(t.subtiles || []).length
                    ? `${(t.subtiles || []).filter((s) => s.enabled !== false).length}${isCondo ? ` ${tx.groupOptions}` : ''}`
                    : ''}
                accent={vis?.accent || 'teal'} surface={surface} compact={compactFor(tier)}
                statusColor={svcStatus[t.id]}
                onClick={() => openTile(t)}
                testId={`assistant-menu-tile-${t.id}`} />);
        });
        // The three group entry tiles, added last so they sit after the live
        // Weather Event tile in the same secondary tier.
        const GROUPS = [
            ['communication', Megaphone, 'purple', tx.groupCommTile, tx.groupCommTag],
            ['building_access', Building2, 'cyan', tx.groupBuildingTile, tx.groupBuildingTag],
            ['services', Recycle, 'green', tx.groupServicesTile, tx.groupServicesTag],
        ];
        GROUPS.forEach(([id, Icon, accent, title, tag]) => {
            const rows = groupRows[id];
            if (!rows.length) return;
            push('secondary', <ServiceTile key={`group-${id}`} icon={<Icon className={icls('secondary')} />}
                title={title} tag={tag} accent={accent} surface={surface} compact={compactFor('secondary')}
                badge={id === 'communication' ? (comm.unread_count || 0) + visitorUnread : 0}
                onClick={() => { setActiveGroup({ id, title, rows }); setPhase('group'); }}
                testId={`assistant-menu-group-${id}`} />);
        });
        const FULL_GRID = 'flex flex-wrap justify-center gap-3 [&>*]:basis-[calc(50%-0.375rem)] md:[&>*]:basis-[calc(33.333%-0.5rem)] lg:[&>*]:basis-[calc(25%-0.5625rem)]';
        const COMPACT_GRID = 'flex flex-wrap justify-center gap-2.5 [&>*]:basis-[calc(50%-0.3125rem)] sm:[&>*]:basis-[calc(33.333%-0.4167rem)] lg:[&>*]:basis-[calc(25%-0.469rem)]';
        const primaryNodes = menu.filter((m) => m.tier === 'primary').map((m) => m.node);
        const secondaryNodes = menu.filter((m) => m.tier === 'secondary').map((m) => m.node);
        return wrap(
            <>
                {header}
                {poolFullMenu && (
                    <button type="button" onClick={() => setPoolFullMenu(false)}
                        className="mb-3 text-sm text-teal-300 hover:text-teal-200 flex items-center gap-1"
                        data-testid="assistant-back-to-pool-btn">← {tx.poolTitle}</button>
                )}
                <div className="flex items-center justify-between mb-3">
                    <p className="text-slate-300 text-sm" data-testid="assistant-room-label">
                        {tx.room} <span className="text-white font-semibold">{session?.room_number}</span>
                    </p>
                    <button type="button" onClick={changeRoom}
                        className="text-xs text-slate-400 hover:text-white underline underline-offset-2 transition-colors"
                        data-testid="assistant-change-room-btn">{tx.changeRoom}</button>
                </div>
                <div className="flex items-center gap-3 mb-5" aria-hidden="true" data-testid="assistant-ornament-divider">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent to-amber-200/30" />
                    <div className="w-1.5 h-1.5 rotate-45 bg-amber-300/50" />
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent to-amber-200/30" />
                </div>
                {!isCondo && session?.welcome_completed === false && session?.welcome_card_id && (
                    <a href={`/hotel/${session.welcome_card_id}`}
                        className="w-full flex items-center gap-3 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/40 rounded-2xl p-4 mb-4 transition-colors"
                        data-testid="assistant-welcome-tile">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-amber-500/25 text-amber-300">
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                            <p className="text-white font-semibold text-sm">{tx.welcomeTitle}</p>
                            <p className="text-amber-200/80 text-xs mt-0.5">{tx.welcomeTag}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-amber-300 shrink-0" />
                    </a>
                )}
                {/* The progress of their OWN open requests, between the unit
                    header and the tile grid. The grid (and its per-tile status
                    dots) is untouched — this sits beside it, not instead. */}
                <HomeRequestsSummary tx={tx} api={API} cardId={cardId} token={session?.token}
                    isCondo={isCondo} surface={surface}
                    tileLabels={Object.fromEntries((cfg.tiles || []).map((t) => [t.id, tl(t, 'label', lang) || t.label_en]))} />
                <div className="flex items-baseline gap-3 mb-4">
                    <p className="text-white font-semibold flex-1">{tx.menuTitle}</p>
                    {isCondo && secondaryNodes.length > 0 && (
                        <button type="button" onClick={() => setFullMenu((f) => !f)}
                            data-testid="assistant-full-menu-toggle"
                            aria-pressed={fullMenu}
                            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold
                                        transition-colors ${fullMenu
                                            ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40'
                                            : 'text-slate-300 border border-white/15 hover:text-white'}`}>
                            <LayoutGrid className="w-3.5 h-3.5" />
                            {fullMenu ? (tx.groupedMenu || 'Grouped') : (tx.fullMenu || 'Full Menu')}
                        </button>
                    )}
                </div>
                <div data-testid="assistant-menu">
                    {/* FULL MENU: one flat grid, every function at once, all at
                        the primary touch-target size — nothing shrinks. */}
                    <div className={FULL_GRID} data-testid="assistant-menu-primary">
                        {isCondo ? (fullMenu ? [...primaryNodes, ...secondaryNodes] : primaryNodes)
                            : menu.map((m) => m.node)}
                    </div>
                    {isCondo && !fullMenu && secondaryNodes.length > 0 && (
                        <>
                            <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mt-7 mb-3"
                                data-testid="assistant-menu-more-label">{tx.homeMore}</p>
                            <div className={COMPACT_GRID} data-testid="assistant-menu-secondary">
                                {secondaryNodes}
                            </div>
                        </>
                    )}
                </div>
            </>,
            'max-w-md md:max-w-3xl lg:max-w-4xl'
        );
    }

    const backToMenu = () => { setPhase('menu'); refreshComm(); };
    const INNER_W = 'max-w-md md:max-w-2xl';
    if (phase === 'group' && activeGroup) return wrap(<PropertyGroupScreen group={activeGroup} tx={tx} onBack={backToMenu} />, INNER_W);
    if (phase === 'property_tiles') return wrap(<PropertyTilesScreen tile={activeTile} tx={tx} lang={lang} post={post} onBack={backToMenu} surface={surface} cardId={cardId} token={session?.token} api={API} />, INNER_W);
    if (phase === 'security') return wrap(<SecurityScreen cfg={cfg.security || {}} tx={tx} post={post} onBack={backToMenu} surface={surface} cardId={cardId} token={session?.token} api={API} />, INNER_W);
    if (phase === 'info') return wrap(<HotelInfoScreen cfg={cfg.hotel_info || {}} tx={tx} lang={lang} onBack={backToMenu} openDest={openDest} surface={surface} />, INNER_W);
    if (phase === 'food') return wrap(<FoodScreen cfg={cfg.food || {}} tx={tx} lang={lang} onBack={backToMenu} openDest={openDest} post={post} surface={surface} />, INNER_W);
    if (phase === 'entertainment') {
        const partners = (cfg.food?.outside_restaurants?.enabled
            ? (cfg.food.outside_restaurants.partners || []) : [])
            .filter((p) => p && p.name && p.url);
        return wrap(<EntertainmentScreen cfg={cfg.entertainment || {}} tx={tx} lang={lang} api={API} cardId={cardId} token={session?.token} onBack={backToMenu} openDest={openDest} surface={surface} partners={partners} />, INNER_W);
    }
    if (phase === 'housekeeping') return wrap(<HousekeepingScreen cfg={cfg.housekeeping || {}} tx={tx} post={post} onBack={backToMenu} surface={surface} />, INNER_W);
    if (phase === 'concierge') return wrap(<ConciergeScreen cfg={cfg.concierge || {}} tx={tx} post={post} onBack={backToMenu} surface={surface} cardId={cardId} token={session?.token} api={API} />, INNER_W);
    if (phase === 'valet') return wrap(<ValetScreen cfg={cfg.valet || {}} tx={tx} api={API} cardId={cardId} token={session?.token} post={post} onBack={backToMenu} surface={surface} />, INNER_W);
    if (phase === 'messages') return wrap(<MessagesScreen tx={tx} lang={lang} api={API} cardId={cardId} token={session?.token} onBack={backToMenu} surface={surface} propertyType={cfg.property_type} terminology={cfg.terminology} />, INNER_W);
    if (phase === 'unit_messages') return wrap(<UnitMessageScreen tx={tx} api={API} cardId={cardId} token={session?.token} myUnit={session?.room_number} isCondo={isCondo} onBack={backToMenu} surface={surface} />, INNER_W);
    if (phase === 'announcements') return wrap(<AnnouncementsScreen tx={tx} api={API} cardId={cardId} token={session?.token} onBack={backToMenu} surface={surface} />, INNER_W);
    if (phase === 'activity') return wrap(<ActivityScreen tx={tx} api={API} cardId={cardId} token={session?.token} onBack={backToMenu} surface={surface} isCondo={isCondo} />, INNER_W);
    if (phase === 'people_actions') return wrap(<PropertyPeopleScreen tx={tx} lang={lang} token={session?.token} onBack={backToMenu} surface={surface} />, INNER_W);
    if (phase === 'visitors') return wrap(<VisitorsScreen tx={tx} lang={lang} token={session?.token} onBack={backToMenu} surface={surface} onUnread={setVisitorUnread} />, INNER_W);
    if (phase === 'offers') return wrap(<OffersScreen tx={tx} api={API} cardId={cardId} token={session?.token} onBack={backToMenu} surface={surface} />, INNER_W);

    return null;
}
